import { useCallback, useEffect, useMemo, useState } from "react";
import { LayoutList } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/shared/ui/button";
import { CARD_WALL, CARD_WALL_CAP } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import { websiteService, type Template, type TemplateSummary } from "../api/websiteService";
import { familyLabel } from "../model/schemaLabels";
import { CreateSiteDialog, type DialogTemplate } from "./components/CreateSiteDialog";
import { HostProfilePanel } from "./components/HostProfilePanel";
import { SchemaChecklist } from "./components/SchemaChecklist";
import { TemplateCard } from "./components/TemplateCard";
import { WebsiteShell } from "./WebsiteShell";

/**
 * 屏 2「模板市场」:family 筛选 chips + 卡墙(cap 档)+ 右栏选中模板的字段清单 + 新建站点弹窗。
 *
 * ★ 选中态只有一个来源(`selectedCode`),默认列表第一张;筛选不改选中 —— 右栏还指着上一张时
 *   用户看得见自己选的是谁,不会因为切了个 chip 清单就换了。
 * ★ 详情(`GET /templates/{code}`)按选中懒拉,回包时校验还是不是当前选中的那张,快速连点不串。
 * ★ 宿主模板(A.46 / A.57,host 模式 api 回的就是宿主模板):右栏换 `HostProfilePanel`(页列表 + 节点计数),不画 schema 分组;
 *   「用这套建站」流程不变(`POST sites {name, templateCode}`,templateCode = 宿主 templateId)。
 */
export default function TemplatesPage() {
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [family, setFamily] = useState<string | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [detail, setDetail] = useState<Template | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailTick, setDetailTick] = useState(0);
  const [dialogFor, setDialogFor] = useState<DialogTemplate | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await websiteService.listTemplates();
      setTemplates(list);
      setSelectedCode((prev) => prev ?? list[0]?.code ?? null);
    } catch (e) {
      setTemplates(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedCode) return;
    let alive = true;
    setDetail(null);
    setDetailError(null);
    websiteService
      .getTemplate(selectedCode)
      .then((t) => {
        if (alive) setDetail(t);
      })
      .catch((e: unknown) => {
        if (alive) setDetailError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [selectedCode, detailTick]);

  const families = useMemo(() => [...new Set((templates ?? []).map((t) => t.family))], [templates]);

  const visible = useMemo(
    () => (templates ?? []).filter((t) => family === null || t.family === family),
    [templates, family]
  );

  const openDialog = (t: DialogTemplate) =>
    setDialogFor({
      code: t.code,
      name: t.name,
      hostTemplate: t.hostTemplate,
      dynamic: t.dynamic,
      articleList: t.articleList,
    });

  const selectedSummary = templates?.find((t) => t.code === selectedCode) ?? null;

  return (
    <WebsiteShell
      actions={
        <Button asChild variant="outline" size="sm">
          <Link to="/ai-website">
            <LayoutList />
            我的站点
          </Link>
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {error ? (
            <QueryErrorBanner isError message={error} onRetry={() => void load()} />
          ) : templates === null ? (
            <div className={CARD_WALL}>
              <div className={CARD_WALL_CAP}>
                <Skeleton className="h-72 w-full" />
                <Skeleton className="h-72 w-full" />
                <Skeleton className="h-72 w-full" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <div
                  role="group"
                  aria-label="按风格筛选"
                  className="flex flex-wrap items-center gap-1.5"
                >
                  <FamilyChip
                    active={family === null}
                    onClick={() => setFamily(null)}
                    label={`全部 ${templates.length}`}
                  />
                  {families.map((f) => (
                    <FamilyChip
                      key={f}
                      active={family === f}
                      onClick={() => setFamily(f)}
                      label={familyLabel(f)}
                    />
                  ))}
                </div>
                <span className="ml-auto text-2xs text-muted-foreground">
                  模板由发布服务器提供,新模板上架即出现
                </span>
              </div>
              {visible.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {templates.length === 0 ? "还没有可用模板。" : "这个风格下暂无模板。"}
                </p>
              ) : (
                <div className={CARD_WALL}>
                  <div className={CARD_WALL_CAP}>
                    {visible.map((t) => (
                      <TemplateCard
                        key={t.code}
                        template={t}
                        selected={t.code === selectedCode}
                        onSelect={() => setSelectedCode(t.code)}
                        onUse={() => {
                          setSelectedCode(t.code);
                          openDialog(t);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {selectedSummary?.hostTemplate ? (
          <HostProfilePanel
            summary={selectedSummary}
            template={detail}
            error={detailError}
            onRetry={() => setDetailTick((n) => n + 1)}
            onStart={() => openDialog(selectedSummary)}
          />
        ) : (
          selectedCode && (
            <SchemaChecklist
              template={detail}
              error={detailError}
              onRetry={() => setDetailTick((n) => n + 1)}
              onStart={() => {
                if (detail) openDialog(detail);
              }}
            />
          )
        )}
      </div>

      <CreateSiteDialog
        open={dialogFor !== null}
        onOpenChange={(o) => {
          if (!o) setDialogFor(null);
        }}
        template={dialogFor}
      />
    </WebsiteShell>
  );
}

function FamilyChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
      )}
    >
      {label}
    </button>
  );
}
